import type { LLMGenerateOptions, LLMMessage } from '@/IsekaiRPG/modules/llm/llmTypes';
import { klona } from 'klona';

export interface MessagePipelineChainTraceSummary {
  totalMessages: number;
  totalChars: number;
  systemMessages: number;
  userMessages: number;
  assistantMessages: number;
  systemChars: number;
  userChars: number;
  assistantChars: number;
}

export interface MessagePipelineChainTrace {
  sourceKind: 'messages' | 'user_input' | 'empty' | 'direct_messages';
  useSystemPrompt: boolean;
  normalizedMessages: LLMMessage[];
  systemEntries: string[];
  nonSystemMessages: LLMMessage[];
  summary: MessagePipelineChainTraceSummary;
}

export class MessagePipeline {
  static normalizeMessages(options: LLMGenerateOptions): LLMMessage[] {
    if (Array.isArray(options.messages) && options.messages.length > 0) {
      return options.messages
        .map(message => ({
          role: message.role,
          content: String(message.content ?? '').trim(),
        }))
        .filter(message => message.content.length > 0);
    }

    const userInput = String(options.user_input ?? '').trim();
    if (userInput.length > 0) {
      return [{ role: 'user', content: userInput }];
    }

    return [];
  }

  static buildChainTrace(options: LLMGenerateOptions, useSystemPrompt: boolean): MessagePipelineChainTrace {
    const normalizedMessages = this.normalizeMessages(options);
    const sourceKind = this.detectSourceKind(options);
    return this.buildChainTraceFromMessages(normalizedMessages, useSystemPrompt, sourceKind);
  }

  static buildChainTraceFromMessages(
    messages: LLMMessage[],
    useSystemPrompt: boolean,
    sourceKind: MessagePipelineChainTrace['sourceKind'] = 'direct_messages',
  ): MessagePipelineChainTrace {
    const normalizedMessages = messages
      .map(message => ({
        role: message.role,
        content: String(message.content ?? '').trim(),
      }))
      .filter(message => message.content.length > 0);
    const { systemEntries, nonSystemMessages } = this.extractSystemEntries(normalizedMessages, useSystemPrompt);

    return {
      sourceKind,
      useSystemPrompt,
      normalizedMessages,
      systemEntries,
      nonSystemMessages,
      summary: this.buildTraceSummary(normalizedMessages, systemEntries),
    };
  }

  private static extractSystemEntries(
    messages: LLMMessage[],
    useSystemPrompt: boolean,
  ): {
    systemEntries: string[];
    nonSystemMessages: LLMMessage[];
  } {
    const cloned = klona(messages);
    if (!useSystemPrompt) {
      return {
        systemEntries: [],
        nonSystemMessages: cloned,
      };
    }

    const systemEntries: string[] = [];
    const nonSystemMessages: LLMMessage[] = [];

    for (const message of cloned) {
      const content = message.content.trim();
      if (!content) {
        continue;
      }

      if (message.role === 'system') {
        systemEntries.push(content);
      } else {
        nonSystemMessages.push({
          role: message.role,
          content,
        });
      }
    }

    return {
      systemEntries,
      nonSystemMessages,
    };
  }

  private static detectSourceKind(options: LLMGenerateOptions): MessagePipelineChainTrace['sourceKind'] {
    if (Array.isArray(options.messages) && options.messages.length > 0) return 'messages';
    const userInput = String(options.user_input ?? '').trim();
    if (userInput.length > 0) return 'user_input';
    return 'empty';
  }

  private static buildTraceSummary(
    normalizedMessages: LLMMessage[],
    systemEntries: string[],
  ): MessagePipelineChainTraceSummary {
    let userMessages = 0;
    let assistantMessages = 0;
    let userChars = 0;
    let assistantChars = 0;

    normalizedMessages.forEach(message => {
      if (message.role === 'assistant') {
        assistantMessages += 1;
        assistantChars += message.content.length;
      } else if (message.role === 'user') {
        userMessages += 1;
        userChars += message.content.length;
      }
    });

    const systemChars = systemEntries.reduce((sum, item) => sum + item.length, 0);
    return {
      totalMessages: normalizedMessages.length,
      totalChars: normalizedMessages.reduce((sum, item) => sum + item.content.length, 0),
      systemMessages: systemEntries.length,
      userMessages,
      assistantMessages,
      systemChars,
      userChars,
      assistantChars,
    };
  }
}
